// Advisor call lead capture. Any `a.js-advisor-call` link (href = Calendly URL)
// opens an on-site form first; on submit we save a ContactLead, then open
// Calendly in a new tab with name/email/details prefilled. The href stays as
// the no-JS fallback.
import { rememberEmail } from '~/javascripts/known-email.js';

const SCRIPT_LOADED_AT = Date.now();

document.addEventListener('DOMContentLoaded', () => {
  const modal = document.getElementById('advisorCallModal');
  const overlay = document.getElementById('advisorCallOverlay');
  const closeBtn = document.getElementById('advisorCallClose');
  const form = document.getElementById('advisorCallForm');
  const successBox = document.getElementById('advisorCallSuccess');
  const successLink = document.getElementById('advisorCallSuccessLink');
  const errorEl = document.getElementById('advisorCallError');

  if (!modal || !overlay || !form) return;

  const DEFAULT_CALENDLY_URL = 'https://calendly.com/client-advisor/quick-15-min-chat';
  let calendlyUrl = DEFAULT_CALENDLY_URL;

  // Fade/scale via the shared modal primitives (ds-global.css); the 300ms
  // hide delay below matches --ds-transition-modal.
  modal.classList.add('ds-modal-anim', 'ds-modal-anim--dialog');
  overlay.classList.add('ds-modal-anim');
  let closeTimer = null;

  function hideOpenDialogs() {
    // Avoid stacking on top of an already-open Bootstrap or advisor-style modal.
    if (window.bootstrap && window.bootstrap.Modal) {
      document.querySelectorAll('.modal.show').forEach((el) => {
        window.bootstrap.Modal.getInstance(el)?.hide();
      });
    }
    document.querySelectorAll('.advisor-modal, .advisor-overlay').forEach((el) => {
      if (el !== modal && el !== overlay && el.style.display && el.style.display !== 'none') {
        el.style.display = 'none';
      }
    });
  }

  function openModal() {
    hideOpenDialogs();
    form.style.display = '';
    successBox.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';

    // Prefill group size from the discover guests filter if the visitor set one.
    const guestsField = document.getElementById('advisor-call-guests');
    if (guestsField && !guestsField.value) {
      const discoverGuests = Array.from(document.querySelectorAll('[data-filter-param="guests"]'))
        .map((el) => (el.value || '').trim())
        .find(Boolean);
      if (discoverGuests) guestsField.value = discoverGuests;
    }

    clearTimeout(closeTimer);
    modal.style.display = 'grid';
    overlay.style.display = 'block';
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      overlay.classList.add('is-open');
    });
    document.body.style.overflow = 'hidden';
    if (window.lucide) window.lucide.createIcons();

    // Focus the first empty field so prefilled visitors land on what's missing.
    const firstEmpty = ['advisor-call-name', 'advisor-call-email', 'advisor-call-guests', 'advisor-call-message']
      .map((id) => document.getElementById(id))
      .find((el) => el && !el.value);
    (firstEmpty || document.getElementById('advisorCallSubmit'))?.focus();
  }

  function closeModal() {
    modal.classList.remove('is-open');
    overlay.classList.remove('is-open');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      modal.style.display = 'none';
      overlay.style.display = 'none';
    }, 300);
    document.body.style.overflow = '';
  }

  function prefilledCalendlyUrl(name, email, guests, message) {
    let url;
    try {
      url = new URL(calendlyUrl);
    } catch {
      url = new URL(DEFAULT_CALENDLY_URL);
    }
    if (name) url.searchParams.set('name', name);
    if (email) url.searchParams.set('email', email);
    const details = [
      guests ? `Group size: ${guests}` : null,
      message || null
    ].filter(Boolean).join(' — ');
    if (details) url.searchParams.set('a1', details);
    return url.toString();
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('a.js-advisor-call');
    if (!trigger) return;
    e.preventDefault();
    calendlyUrl = trigger.getAttribute('href') || DEFAULT_CALENDLY_URL;
    openModal();
  });

  closeBtn?.addEventListener('click', closeModal);
  overlay.addEventListener('click', closeModal);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.style.display === 'grid') closeModal();
  });

  // Keep focus inside the dialog while it is open.
  modal.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusables = modal.querySelectorAll(
      'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    const visible = Array.from(focusables).filter((el) => el.offsetParent !== null);
    if (visible.length === 0) return;
    const first = visible[0];
    const last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    const name = document.getElementById('advisor-call-name')?.value?.trim() || '';
    const email = (document.getElementById('advisor-call-email')?.value || '').trim().toLowerCase();
    const guests = document.getElementById('advisor-call-guests')?.value?.trim() || '';
    const message = document.getElementById('advisor-call-message')?.value?.trim() || '';

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!name || !emailValid) {
      if (errorEl) errorEl.style.display = 'block';
      return;
    }
    if (errorEl) errorEl.style.display = 'none';
    rememberEmail(email);

    // Open Calendly synchronously (user gesture) so popup blockers allow it.
    const handoffUrl = prefilledCalendlyUrl(name, email, guests, message);
    window.open(handoffUrl, '_blank', 'noopener');
    if (successLink) successLink.href = handoffUrl;

    // Persist the lead; keepalive lets it finish even if the user navigates.
    const body = new FormData();
    body.append('name', name);
    body.append('email', email);
    if (guests) body.append('guests', guests);
    if (message) body.append('message', message);
    body.append('source', 'advisor_call');
    body.append('website', form.querySelector('[name="website"]')?.value || '');
    // Bot-signal timing field (see shared/_lead_form_guard): ms since script load.
    body.append('ft', String(Date.now() - SCRIPT_LOADED_AT));
    fetch('/contact_leads', { method: 'POST', body, keepalive: true }).catch(() => {});

    form.style.display = 'none';
    successBox.style.display = 'block';
    // Land the confirmation with a small pop.
    successBox.classList.add('ds-anim-pop');
    successBox.addEventListener(
      'animationend',
      () => successBox.classList.remove('ds-anim-pop'),
      { once: true }
    );
  });
});
